import { useMemo, useState } from 'react';
import { DeleteOutlined, FileDoneOutlined } from '@ant-design/icons';

import { Badge, Button, Checkbox, Empty, Input, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import {
  BA_TRANG_THAI,
  bangCanGui,
  bangHien,
  boLoiKhai,
  congDonCua,
  phamViCua,
  type BaTrangThai,
  type BangKhai,
} from '../../nghiep-vu/da-khai';
import { useBangDaKhai, useLuuBang } from '../../hooks/useKho';
import { useMoTaLoi } from '../../hooks/useMoTaLoi';
import { DaiLoi } from '../chung/VungDuLieu';
import { HopXacNhan } from '../chung/HopXacNhan';

/**
 * Những gì ĐÃ khai, sửa được tại chỗ (port `DaKhaiTab.tsx` native).
 *
 * Bộ sinh câu hỏi CỐ Ý chỉ hỏi chỗ còn trống ("hỏi lại thứ đã khai là cách nhanh nhất để người vận hành bỏ giữa chừng"),
 * nên khai xong là câu biến mất — tab này là chỗ duy nhất còn soát lại và sửa được lời khai. Lưu chỉ gửi bảng có ô đang
 * treo (upsert, không xoá bảng vắng mặt) nhưng gửi NGUYÊN object bảng — xem `bangCanGui`.
 */
export function DaKhaiTab({ sourceId, xoaDuoc }: { sourceId: string; xoaDuoc: boolean }) {
  const { t, locale } = useT();
  const toast = useToast();
  const moTaLoi = useMoTaLoi();
  const ds = useBangDaKhai(sourceId);
  const ghi = useLuuBang(sourceId);
  const xoa = useLuuBang(sourceId);

  /** Sửa đang treo, khoá `"<bảng>.<cột>.<trường>"`. Chưa bấm Lưu thì chưa ghi. */
  const [sua, setSua] = useState<Record<string, string>>({});
  const [dangMo, setDangMo] = useState<string | null>(null);
  const [chiBangTat, setChiBangTat] = useState(false);
  /** Bảng đang chờ xác nhận xoá lời khai. */
  const [xoaTen, setXoaTen] = useState<string[] | null>(null);
  const [chon, setChon] = useState<Set<string>>(new Set());

  const raw = useMemo(() => (ds.data ?? []) as unknown as BangKhai[], [ds.data]);
  const bangs = useMemo(() => bangHien(raw, chiBangTat), [raw, chiBangTat]);
  const soBangTat = raw.filter((b) => b.enabled === false).length;
  const soSua = Object.keys(sua).length;

  if (ds.isPending) return <Skeleton className="h-40 w-full" />;
  if (ds.error) return <DaiLoi loi={ds.error} onThuLai={() => void ds.refetch()} />;
  if (!bangs.length && !chiBangTat) {
    return (
      <Empty icon={FileDoneOutlined} title={t('troLyAiKhoDaKhai.rong')} description={t('troLyAiKhoDaKhai.rongMo')} />
    );
  }

  const luu = () =>
    ghi.mutate(
      { tables: bangCanGui(raw, sua) as unknown as Record<string, unknown>[] },
      {
        onSuccess: (r) => {
          toast.success(t('troLyAiKhoDaKhai.daLuu', { n: soVN(r.saved, locale) }));
          setSua({});
        },
        onError: (e) => toast.error(t('troLyAiKhoDaKhai.khongLuuDuoc', { loi: moTaLoi(e) })),
      },
    );

  /**
   * Xoá ở đây là xoá LỜI KHAI, không phải xoá bảng: ghi lại chính bảng ấy với phần lời khai để trắng, cấu trúc giữ nguyên.
   * Gỡ bảng khỏi bản khai là mất luôn lịch đồng bộ và phân quyền — người bấm ở đây chỉ muốn khai lại nghiệp vụ.
   */
  const xacNhanXoa = (ten: string[]) =>
    xoa.mutate(
      { tables: raw.filter((x) => ten.includes(x.name)).map(boLoiKhai) as unknown as Record<string, unknown>[] },
      {
        onSuccess: () => {
          toast.success(
            ten.length === 1
              ? t('troLyAiKhoDaKhai.daXoaKhai', { bang: ten[0] ?? '' })
              : t('troLyAiKhoDaKhai.daXoaKhaiNhieu', { n: soVN(ten.length, locale) }),
          );
          setSua((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !ten.some((b) => k.startsWith(`${b}.`)))));
          setChon((p) => new Set([...p].filter((b) => !ten.includes(b))));
          setXoaTen(null);
        },
      },
    );

  const o = (k: string, cu: string | null | undefined) => (k in sua ? (sua[k] ?? '') : (cu ?? ''));
  const dat = (k: string, v: string) => setSua((p) => ({ ...p, [k]: v }));
  const daChonHet = bangs.length > 0 && bangs.every((b) => chon.has(b.name));
  const tich = (ten: string, v: boolean) =>
    setChon((p) => {
      const n = new Set(p);
      if (v) n.add(ten);
      else n.delete(ten);
      return n;
    });

  const nhanBa = (v: BaTrangThai, co: string, khong: string, chua: string) => (v === 'co' ? co : v === 'khong' ? khong : chua);

  const nhomNut = ({ k, hienTai, co, khong, chua }: { k: string; hienTai: BaTrangThai; co: string; khong: string; chua: string }) => {
    const dang = (k in sua ? sua[k] : hienTai) as BaTrangThai;
    return (
      <div className="flex flex-wrap gap-1.5" role="group">
        {BA_TRANG_THAI.map((v) => (
          <button
            key={v || 'chua'}
            type="button"
            aria-pressed={dang === v}
            onClick={() => dat(k, v)}
            className={cn(
              'rounded border px-2.5 py-1 text-xs transition-colors',
              dang === v
                ? 'border-primary-300 bg-primary-50 font-medium text-primary-700'
                : 'border-default bg-surface text-content-secondary hover:bg-subtle',
            )}
          >
            {nhanBa(v, co, khong, chua)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {xoaDuoc && bangs.length > 0 ? (
          <Checkbox
            aria-label={t('troLyAiKhoDaKhai.chonHet')}
            checked={daChonHet}
            onChange={(e) => setChon(e.target.checked ? new Set(bangs.map((b) => b.name)) : new Set())}
          />
        ) : null}
        <p className="min-w-0 flex-1 text-xs text-content-secondary">{t('troLyAiKhoDaKhai.moTa')}</p>
        {soBangTat > 0 ? (
          <Checkbox checked={chiBangTat} onChange={(e) => setChiBangTat(e.target.checked)}>
            <span className="text-xs text-content-secondary">
              {t('troLyAiKhoDaKhai.chiBangTat', { n: soVN(soBangTat, locale) })}
            </span>
          </Checkbox>
        ) : null}
        {xoaDuoc && chon.size > 0 ? (
          <Button size="sm" variant="destructive" icon={<DeleteOutlined />} onClick={() => setXoaTen([...chon])}>
            {t('troLyAiKhoDaKhai.xoaDaChon', { n: soVN(chon.size, locale) })}
          </Button>
        ) : null}
        {soSua > 0 ? <Badge variant="warning">{t('troLyAiKhoDaKhai.choLuu', { n: soVN(soSua, locale) })}</Badge> : null}
        <Button size="sm" disabled={!soSua} loading={ghi.isPending} onClick={luu}>
          {t('troLyAiKhoDaKhai.luu')}
        </Button>
      </div>

      {bangs.map((b) => {
        const mo = dangMo === b.name;
        return (
          <div key={b.name} className="rounded-lg border border-default bg-surface">
            {/* Nút xoá nằm NGOÀI nút mở/đóng: `<button>` lồng `<button>` là HTML không hợp lệ, bấm Xoá lại mở bảng ra. */}
            <div className={cn('flex items-center hover:bg-subtle', mo ? 'rounded-t-lg' : 'rounded-lg')}>
              {xoaDuoc ? (
                <span className="ms-3.5 shrink-0">
                  <Checkbox aria-label={b.name} checked={chon.has(b.name)} onChange={(e) => tich(b.name, e.target.checked)} />
                </span>
              ) : null}
              <button
                type="button"
                aria-expanded={mo}
                className="flex min-w-0 flex-1 flex-wrap items-center gap-2 px-3.5 py-2.5 text-start"
                onClick={() => setDangMo(mo ? null : b.name)}
              >
                <span className="min-w-0 truncate text-sm font-medium text-content-primary">{b.name}</span>
                {b.enabled === false ? <Badge variant="neutral">{t('troLyAiKhoDaKhai.tat')}</Badge> : null}
                <span className="flex-1" />
                <span className="text-xs text-content-secondary">
                  {t('troLyAiKhoDaKhai.soCotDaKhai', { n: soVN(b.cot.length, locale) })}
                </span>
              </button>
              {xoaDuoc ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="me-2 shrink-0"
                  aria-label={t('troLyAiKhoDaKhai.xoaKhai')}
                  title={t('troLyAiKhoDaKhai.xoaKhai')}
                  icon={<DeleteOutlined />}
                  onClick={() => setXoaTen([b.name])}
                />
              ) : null}
            </div>

            {mo ? (
              <div className="space-y-3 border-t border-default px-3.5 py-3">
                <div>
                  <span className="mb-1 block text-xs font-medium text-content-secondary">
                    {t('troLyAiKhoDaKhai.phamViHoi')}
                  </span>
                  {nhomNut({
                    k: `${b.name}..phamVi`,
                    hienTai: phamViCua(b),
                    co: t('troLyAiKhoDaKhai.phamViCo'),
                    khong: t('troLyAiKhoDaKhai.phamViKhong'),
                    chua: t('troLyAiKhoDaKhai.phamViChua'),
                  })}
                </div>

                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-content-secondary">{t('troLyAiKhoDaKhai.grain')}</span>
                  <Input
                    value={o(`${b.name}..grain`, b.grain)}
                    placeholder={t('troLyAiKhoDaKhai.chuaKhai')}
                    onChange={(e) => dat(`${b.name}..grain`, e.target.value)}
                  />
                </label>

                {b.cot.map((c) => (
                  <div key={c.name} className="rounded-lg border border-default p-2.5">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <code className="text-xs font-medium text-content-primary">{c.name}</code>
                      {c.type ? <Badge variant="neutral">{c.type}</Badge> : null}
                      {c.enumValues?.length ? (
                        <Badge variant="primary">
                          {t('troLyAiKhoDaKhai.soMa', { n: soVN(c.enumValues.length, locale) })}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {/* Cộng dồn: BA trạng thái. "Chưa khai" bấm được — nó là đường đưa câu hỏi về lại màn Phỏng vấn,
                          kèm bằng chứng số liệu mà màn này không có. */}
                      <div className="sm:col-span-2">
                        <span className="mb-1 block text-xs text-content-secondary">
                          {t('troLyAiKhoDaKhai.congDonHoi')}
                        </span>
                        {nhomNut({
                          k: `${b.name}.${c.name}.additive`,
                          hienTai: congDonCua(c.additive),
                          co: t('troLyAiKhoDaKhai.congDonCo'),
                          khong: t('troLyAiKhoDaKhai.congDonKhong'),
                          chua: t('troLyAiKhoDaKhai.congDonChua'),
                        })}
                      </div>
                      <label className="block">
                        <span className="mb-1 block text-xs text-content-secondary">{t('troLyAiKhoDaKhai.nhan')}</span>
                        <Input
                          value={o(`${b.name}.${c.name}.label`, c.label)}
                          placeholder={t('troLyAiKhoDaKhai.chuaKhai')}
                          onChange={(e) => dat(`${b.name}.${c.name}.label`, e.target.value)}
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs text-content-secondary">{t('troLyAiKhoDaKhai.donVi')}</span>
                        <Input
                          value={o(`${b.name}.${c.name}.unit`, c.unit)}
                          placeholder={t('troLyAiKhoDaKhai.chuaKhai')}
                          onChange={(e) => dat(`${b.name}.${c.name}.unit`, e.target.value)}
                        />
                      </label>
                      <label className="block sm:col-span-2">
                        <span className="mb-1 block text-xs text-content-secondary">
                          {t('troLyAiKhoDaKhai.trongNghiaLa')}
                        </span>
                        <Input
                          value={o(`${b.name}.${c.name}.nullMeaning`, c.nullMeaning)}
                          placeholder={t('troLyAiKhoDaKhai.chuaKhai')}
                          onChange={(e) => dat(`${b.name}.${c.name}.nullMeaning`, e.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}

      {/* Câu hỏi nói ra ĐÚNG cái mất đi: ở đây là lời khai, không phải dữ liệu — khác nút xoá bảng ở tab Kết nối & đồng bộ. */}
      <HopXacNhan
        open={xoaTen !== null}
        onClose={() => {
          setXoaTen(null);
          xoa.reset();
        }}
        tieuDe={
          xoaTen?.length === 1
            ? t('troLyAiKhoDaKhai.xacNhanXoaKhai', { bang: xoaTen[0] ?? '' })
            : t('troLyAiKhoDaKhai.xacNhanXoaKhaiNhieu', { n: soVN(xoaTen?.length ?? 0, locale) })
        }
        than={t('troLyAiKhoDaKhai.xacNhanXoaKhaiGhi')}
        nhanXacNhan={t('troLyAiKhoDaKhai.xoaKhai')}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onXacNhan={() => xoaTen && xacNhanXoa(xoaTen)}
      />
    </div>
  );
}
